import { Button, Tooltip, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { getDeletedUsers, restoreUser } from "api/services/users";
import Loader from "components/Loader";
import Table, { ColumnType } from "components/Table";
import { snack } from "components/toast";
import { useConfirm } from "context/ConfirmDialog";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { ResType } from "types";
import { handleError } from "utils/handleError";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { exportDeletedUsersReport } from "api/services/reports";
import SearchContainer from "components/SearchContainer";
import { enableBulkGstrClient, enableGstrClient, exportdeletedGstClient, getDeletedGstrClients } from "api/services/gstr/config";
import HelpOutlineIcon from '@mui/icons-material/HelpCenterOutlined';
import { getCurrentFormattedDateTime, getTitle } from "utils";
import { useUserData } from "context/UserProfile";
import { usePermissions } from "context/PermissionsProvider";
import { Permissions } from "data/permissons";
import PermissionDenied from "components/PemissionDenied";
import { SortDirection } from "data/constants";
import sortHandler from "components/Table/SortHandler";
import _ from "lodash";
import moment from "moment";
import excelIcon from "./../../assets/images/128px-Microsoft_Office_Excel_(2019–2025).svg.png";
import ValidateAccess from "components/ValidateAccess";


function DeletedGstrClients(state) {
  const [selected, setSelected] = useState<any[]>([]);
  const confirm = useConfirm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [search, setSearch] = useState("");
  const { data: userData } = useUserData();
  const { permissions } = usePermissions();
  const [sort, setSort] = useState<any>({});
  const { data, isLoading }: ResType = useQuery(["deleted-gstr", {
    limit: pageCount,
    offset: page * pageCount,
    search: search, sort
  }], getDeletedGstrClients, {
    onSuccess: (res) => {
      setDeletedUsers(res?.data?.result);
      setTotalCount(res?.data?.count)
    },
    onError: (err: any) => {
      if (!err?.error) {
        snack.error(`An error occurred while fetching deleted users`);
      }
    }
  });

  const { mutate: mutate1, isLoading: isExporting } = useMutation(exportdeletedGstClient, {
    onSuccess: (res: any) => {
      const arr = new Uint8Array(res.data?.data);
      const blob = new Blob([arr], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const file = window.URL.createObjectURL(blob);
      let link = document.createElement("a");
      const formattedDateTime = getCurrentFormattedDateTime();
      link.href = file;
      link.download = `ATOM Pro - Deleted GST Clients ${formattedDateTime}.xlsx`;
      link.click();
      snack.success("Exported successfully");
    },
    onError: (err: any) => {
      snack.error(err);
      snack.error(handleError(err));
    },
  });

  const handleExport = () => {
    if (isExporting) return;
    mutate1({
      ...state,
      search: search

    });
  }
  const { mutate } = useMutation(enableGstrClient, {
    onSuccess: () => {
      snack.success("Successfully ENABLED Atom Pro for Gstr client.");
      queryClient.invalidateQueries("deleted-gstr");
    },
    onError: (err: any) => {
      if (err && !err?.error) {
        snack.error(handleError(err));
      } else if (!err?.error) {
        snack.error(`An error occurred while restoring users`);
      }
    },
  });

  const { mutate: bulkMutate } = useMutation(enableBulkGstrClient, {
    onSuccess: () => {
      setSelected([]);
      snack.success("Successfully ENABLED Atom Pro for Gstr client.");
      queryClient.invalidateQueries("deleted-gstr");
    },
    onError: (err: any) => {
      if (err && !err?.error) {
        snack.error(handleError(err));
      } else if (!err?.error) {
        snack.error(`An error occurred while restoring users`);
      }
    },
  });

  const handleRestore = (id: number) => {
    confirm({
      msg: "Are you sure you want to ENABLE Gstr Client from Atom Pro?",
      action: () => mutate(id),
    });
  };

  const defaultColumns: Array<ColumnType> = [
    {
      key: "client.clientId",
      title: "Client ID",
      sort: true,
      active: false,
      direction: SortDirection.ASC, default: true,
    },
    {
      key: "client.clientNumber",
      title: "Client #",
      sort: true,
      active: false,
      direction: SortDirection.ASC, default: true,
    },
    {
      key: 'profile.gstin',
      title: 'GSTIN',
      render: (row) => <Typography>{row?.profile?.gstin}</Typography>
    },
    {
      key: "category",
      title: "Category",
      // sort: true,
      // active: false,
      // direction: SortDirection.ASC, default: true,

      render: (rowData) => getTitle(rowData?.client?.newCategory?.name),
    },
    {
      key: "subCategory",
      title: "Sub Category",
      render: (rowData) => getTitle(rowData?.client?.newSubCategory?.name),
    },
    {
      key: "client.displayName",
      title: "Client Name",
      sort: true,
      active: false,
      direction: SortDirection.ASC, default: true,
    },
    {
      key: "userName",
      title: "User Id",
    },
    {
      key: "updatedAt",
      title: "Last Updated at",
      sort: true,
      active: false,
      direction: SortDirection.ASC,
      default: true,
      render: (row) => {
        return moment(row?.updatedAt).format("DD-MM-YYYY HH:mm ");
      },
    },
  ];

  if (userData?.role?.name === "Admin" || permissions.includes(Permissions.ENABLE_DELETED_GST_CLIENTS)) {
    defaultColumns.push({
      key: "Action",
      title: "Action",
      render: (item: any) => {
        return (
          <ValidateAccess name={Permissions.ENABLE_DISABLE}>
          <Button
            size="small"
            variant="contained"
            color="secondary"
            onClick={() => handleRestore(item?.id)}
          >
            ENABLE
          </Button>
          </ValidateAccess>
        );
      },
    })
  }

  const handleDelete = () => {
    confirm({
      msg: "Are you sure you want to ENABLE Gstr Client from Atom Pro?",
      action: () => {
        bulkMutate({
          gstrClients: selected
        });
      },
    });
  };

  const [columns, setColumns] = useState(_.cloneDeep(defaultColumns));
  const handleSort = (v: any) => {
    sortHandler({
      key: v.key,
      columns,
      sortState: sort,
      setSortState: setSort,
      setColumns: setColumns,
    });
  };

  if (isLoading) return <Loader />;

  if (userData?.role?.name !== "Admin" && !permissions.includes(Permissions.VIEW_DELETED_GST_CLIENTS)) {
    return (
      <PermissionDenied />
    );
  }

  return (
    <Box>
      <Box display="flex" gap={3} justifyContent="space-between" mt={2} mb={1} px={1}>
        <SearchContainer
          debounced
          value={search}
          placeHolder="Search by Client Name | PAN"
          onChange={setSearch}
        />
        <Box ml={2} display={"flex"}>
          <ValidateAccess name={Permissions.ENABLE_DISABLE}>
          {selected.length > 0 && (userData?.role?.name === "Admin" || permissions.includes(Permissions.ENABLE_DELETED_GST_CLIENTS)) && <Box><Button variant="outlined" color="secondary" sx={{ mr: 1, mt: "5px" }} onClick={handleDelete}>
            Enable
          </Button></Box>}
          </ValidateAccess>
          <Button  >
            <Button>
              <Tooltip title={isExporting ? "Exporting..." : "Data Export to Excel"}>
                <img
                  src={excelIcon}
                  alt="Export"
                  style={{
                    cursor: isExporting ? "not-allowed" : "pointer",
                    width: 35,
                    height: 35,
                    marginTop: -10,
                    opacity: isExporting ? 0.5 : 1,
                    transition: "opacity 0.2s ease",
                  }}
                  onClick={handleExport}
                />
              </Tooltip>
            </Button>

          </Button>
        </Box>
      </Box>
      <Box px={1}>
        <Table
          sx={{ mb: 0, pb: 0, height: '520px' }}
          loading={isLoading}
          data={deletedUsers || []}
          selection={{ selected, setSelected }}
          // onRowClick={handleRowClick}
          columns={columns}
          sortHandler={handleSort}
          pagination={{ totalCount, page, setPage, pageCount, setPageCount }}
        />
      </Box>
    </Box>
  );
}

export default DeletedGstrClients;
